import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import * as SearchBoardActions from '@store/search-board/search-board.actions';
import { SpinnerOverlayService } from '@shared/services/spinner-overlay.service';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import {
  ExportCustomGridInfo,
  ExportGridInfo,
  ExportTemplate,
  FileLink,
  PropertyInformation,
  SearchRequestTypes,
  SidePanelTab
} from '@store/search-board/search-board.model';
import { ExportService } from '@root/search/services/export.service';
import { CustomizeExportComponent } from '@root/search/components/customize-export/customize-export.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { QuickExportModalComponent } from '@shared/components/action-bar/quick-export-modal/quick-export-modal.component';
import { ConfirmationMessageType, ModalType } from '@shared/models/modal.model';
import { DataElement } from '@store/user/user.model';
import { ActionName } from '@shared/models/effects-names.model';
import { RibbonService } from '@shared/services/ribbon.service';
import { RIBBON_TYPE } from '@shared/models/ribbon.model';
import {
  selectIsPremiumViewAllowed,
  selectMySearchFieldsData,
  selectQuickSearchFieldsData
} from '@store/search-board/search-board.selector';
import { select, Store } from '@ngrx/store';
import { DISPLAY_FIELDS, PremiumFieldCodes, TOTAL_RECORDS } from '@root/search/models/display-fields.constant';
import { ConfirmationService } from '@shared/services/confirmation.service';

@Injectable()
export class ExportEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly dialog: MatDialog,
    private readonly exportService: ExportService,
    private readonly spinnerOverlayService: SpinnerOverlayService,
    private readonly ribbonService: RibbonService,
    private readonly store$: Store,
    private readonly confirmationService: ConfirmationService,
  ) {
  }

  startQuickExport$: Observable<SearchBoardActions.OpenQuickExportModal | SearchBoardActions.GetGridExportInfo> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.START_QUICK_EXPORT),
        switchMap((action: SearchBoardActions.StartQuickExport) => [
          new SearchBoardActions.OpenQuickExportModal(action.payload),
          new SearchBoardActions.GetGridExportInfo()
        ])
      );
  });

  startCustomizeExport$: Observable<SearchBoardActions.OpenCustomizeExportModal | SearchBoardActions.GetCustomExportInfo> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.START_CUSTOM_EXPORT),
        switchMap((action: SearchBoardActions.StartCustomExport) => {
          return [
            new SearchBoardActions.OpenCustomizeExportModal(action.payload),
            new SearchBoardActions.GetCustomExportInfo()
          ];
        })
      );
  });

  startPostcardsExport$: Observable<unknown> = createEffect(() => {
    return this.actions$.pipe(
      ofType(SearchBoardActions.START_POSTCARDS_EXPORT),
      this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.StartPostcardsExport) => {
        return this.exportService.postCard(action.payload)
          .pipe(
            catchError(() => {
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Cannot export data right now. Please try again later.');
              return of(void (0));
            }),
          )
      }, ActionName.START_POSTCARDS_EXPORT),
      switchMap((response: { downloadScvPath: string, realMailersUrl: string }) => {
        return this.confirmDownload(response.realMailersUrl);
      }),
    )
  }, { dispatch: false });

  openCustomizeExportModal$: Observable<SearchBoardActions.QuickSearch | SearchBoardActions.MySearch | SearchBoardActions.RunExport> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.OPEN_CUSTOMIZE_EXPORT_MODAL),
        switchMap((action: SearchBoardActions.OpenCustomizeExportModal) => {
          return this.dialog.open(CustomizeExportComponent, {
            panelClass: ModalType.LARGE,
            data: {
              checkedPropertiesCount: action.payload.checkedProperties.length,
              shouldUseCheckedPropertiesCount: action.payload.shouldUseCheckedPropertiesCount
            }
          })
            .afterClosed()
            .pipe(
              filter(({ range, selectedTab, dataElements }) => range && selectedTab && dataElements && !!dataElements.length),
              map((result) => [result, action.payload])
            )
        }),
        withLatestFrom(
          this.store$.pipe(select(selectQuickSearchFieldsData)),
          this.store$.pipe(select(selectMySearchFieldsData))
        ),
        map(([[ { range, selectedTab, dataElements }, selectedRecords ], quickSearchFields, mySearchFields ]) => {
        if (selectedRecords.shouldUseCheckedPropertiesCount) {
          const records = selectedRecords.checkedProperties.slice(range.from - 1, range.to);
          const propertyIdentifiers = records.map((selectedRecord: PropertyInformation) => selectedRecord.propertyIdentifier);
          return new SearchBoardActions.RunExport({ dataElements, propertyIdentifiers });
        } else if (selectedTab === SidePanelTab.quickSearch) {
          return new SearchBoardActions.QuickSearch({
            searchFields: quickSearchFields,
            displayFields: DISPLAY_FIELDS,
            totalRecords: TOTAL_RECORDS,
            range: range,
            searchRequestType: SearchRequestTypes.EXPORT_RESULT,
            exportResults: true,
            dataElements
          })
        } else {
          return new SearchBoardActions.MySearch({
            searchFields: mySearchFields,
            searchRequestType: SearchRequestTypes.EXPORT_RESULT,
            range: range,
            exportResults: true,
            dataElements
          });
        }
      })
      );
  });

  openQuickExportModal$: Observable<SearchBoardActions.RunExport> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.OPEN_QUICK_EXPORT_MODAL),
        switchMap((action: SearchBoardActions.OpenQuickExportModal) => {
          return this.dialog.open(QuickExportModalComponent, {
            panelClass: ModalType.SMALL,
            data: {
              checkedPropertiesCount: action.payload.length
            }
          })
            .afterClosed()
            .pipe(
              filter<Array<DataElement>>(Boolean),
              map<Array<DataElement>, Array<DataElement>>((columns: Array<DataElement>) => {
                return columns.map((column: DataElement) => {
                  return {
                    ...column,
                    childElements: [] as Array<DataElement>,
                    label: column.label
                  };
                });
              }),
              withLatestFrom(this.store$.pipe(select(selectIsPremiumViewAllowed))),
              map(([result, isPremiumViewAllowed]) => {
                return result.filter((header) => {
                  return !(!isPremiumViewAllowed && !!PremiumFieldCodes.find((i) => i === header.fieldCode));
                });
              }),
              map((result) => [result, action.payload])
            );
        }),
        map(([dataElements, selectedRecords]: [Array<DataElement>, Array<PropertyInformation>]) => {
          const propertyIdentifiers = selectedRecords.map((selectedRecord: PropertyInformation) => {
            return selectedRecord.propertyIdentifier;
          });
          return new SearchBoardActions.RunExport({dataElements, propertyIdentifiers});
        })
      );
  });

  getGridExportInfo$: Observable<SearchBoardActions.GetGridExportInfoSuccess> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.GET_GRID_EXPORT_INFO),
        this.spinnerOverlayService.wrapRequest(() => {
          return this.exportService.getGridExport().pipe(
            catchError((data: ExportGridInfo) => of(data))
          );
        }, ActionName.GRID_EXPORT_INFO),
        map((results: ExportGridInfo) => new SearchBoardActions.GetGridExportInfoSuccess(results))
      );
  });

  getCustomExportInfo$: Observable<SearchBoardActions.GetCustomExportInfoSuccess> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.GET_CUSTOM_EXPORT_INFO),
        this.spinnerOverlayService.wrapRequest(() => {
          return this.exportService.getCustomExport().pipe(
            catchError((data: ExportCustomGridInfo) => of(data))
          );
        }, ActionName.CUSTOM_EXPORT_INFO),
        map((results: ExportCustomGridInfo) => new SearchBoardActions.GetCustomExportInfoSuccess(results))
      );
  });

  createCustomExport$: Observable<SearchBoardActions.CreateCustomExportSuccess> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.CREATE_CUSTOM_EXPORT),
        this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.CreateCustomExport) => {
          return this.exportService.saveExportTemplate(action.payload).pipe(
            tap(() => this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, `${action.payload.label} has been saved.`)),
            catchError(() => {
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, `${action.payload.label} cannot be saved right now.`);
              return of(void (0));
            })
          );
        }, ActionName.CREATE_CUSTOM_EXPORT),
        map((result: ExportTemplate) => new SearchBoardActions.CreateCustomExportSuccess(result))
      );
  });

  updateCustomExport$: Observable<SearchBoardActions.UpdateCustomExportSuccess> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.UPDATE_CUSTOM_EXPORT),
        this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.UpdateCustomExport) => {
          return this.exportService.updateExportTemplate(action.payload).pipe(
            tap(() => this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, `${action.payload.label} has been updated.`)),
            catchError(() => {
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, `${action.payload.label} cannot be updated right now.`);
              return of(void (0));
            })
          );
        }, ActionName.UPDATE_CUSTOM_EXPORT),
        map((result: ExportTemplate) => new SearchBoardActions.UpdateCustomExportSuccess(result))
      );
  });

  deleteCustomExport$: Observable<SearchBoardActions.DeleteCustomExportSuccess> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.DELETE_CUSTOM_EXPORT),
        this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.DeleteCustomExport) => {
          return this.exportService.deleteExportTemplates(action.payload).pipe(
            map(() => new SearchBoardActions.DeleteCustomExportSuccess(action.payload)),
            tap(() => this.ribbonService.openRibbon(RIBBON_TYPE.SUCCESS, `Export has been deleted.`)),
            catchError(() => {
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Cannot delete export. Please try again later.');
              return of(void (0));
            })
          );
        }, ActionName.DELETE_CUSTOM_EXPORT)
      );
  });

  runExport$: Observable<unknown> = createEffect(() => {
    return this.actions$
      .pipe(
        ofType(SearchBoardActions.RUN_EXPORT),
        this.spinnerOverlayService.wrapRequest((action: SearchBoardActions.RunExport) => {
          return this.exportService.generateExportLink(action.payload).pipe(
            tap((exportResponse: FileLink) => this.exportService.openLink(exportResponse.fileLink)),
            catchError(() => {
              this.ribbonService.openRibbon(RIBBON_TYPE.ERROR, 'Cannot export data right now. Please try again later.');
              return of(void (0));
            })
          );
        }, ActionName.RUN_EXPORT)
      );
  }, {dispatch: false});

  private confirmDownload(realMailersUrl: string): Observable<boolean> {
    return this.confirmationService.openConfirmationModal({
      confirmButtonLabel: 'Export',
      messageTitle: 'Click to Export Your Selection',
      message: '',
      messageType: ConfirmationMessageType.INFO,
      title: 'Export'
    }).pipe(
      filter<boolean>(Boolean),
      tap(() => {
        window.open(realMailersUrl);
      })
    );
  }
}
